// utils/membershipStatus.js
//
// C# `MembershipRequestStatusTypes` (Organization.cs L350): Sent=0, Accepted=1,
// Declined=2. Carried by `OrgMembershipRequest.Status` and
// `OrgMembershipInvite.Status`.
//
// The API serialises it as a NUMBER, and the Org Admin page assumed a string.
// That crashed the whole page for any org that actually had requests or
// invitations:
//
//     (a.status || '').localeCompare(b.status || '')
//     TypeError: (a.status || "").localeCompare is not a function
//
// `a.status || ''` looks like a guard but is not one — a number survives it
// untouched (only 0 falls through to ''), and numbers have no `localeCompare`
// or `toLowerCase`. The throw happened during render, so React unmounted the
// route and Next showed its bare "a client-side exception has occurred".
// Reported live on 2026-08-08; same family as the orgAccountType bug — see
// [orgAccountType.js] for the identical trap on a different enum.
//
// NOTE THE MEMBER NAMES. The page was checking for 'pending', 'approved' and
// 'rejected'. None of those exist in this enum — a brand-new request is **Sent**,
// not Pending. Even with string values those branches could never have matched,
// so every row fell through to the grey default badge.
//
// Both wire forms are accepted, so a future switch to string enums (the API does
// use JsonStringEnumConverter elsewhere) will not break callers again.

export const MEMBERSHIP_STATUS_NAMES = {
    0: 'sent',
    1: 'accepted',
    2: 'declined',
};

/** Display form, matching the C# member names exactly (Razor prints enum names). */
export const MEMBERSHIP_STATUS_LABELS = {
    0: 'Sent',
    1: 'Accepted',
    2: 'Declined',
};

/**
 * Normalise a status to a lowercase name.
 * @returns {string} 'sent' | 'accepted' | 'declined'; '' when absent/unknown.
 */
export function membershipStatusStr(raw) {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'number' || /^\d+$/.test(String(raw).trim())) {
        return MEMBERSHIP_STATUS_NAMES[Number(raw)] ?? '';
    }
    return String(raw).toLowerCase().replace(/\s+/g, '');
}

/**
 * Human-readable status. Falls back to the raw value so an unmapped future
 * member still shows something rather than vanishing.
 */
export function membershipStatusLabel(raw) {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'number' || /^\d+$/.test(String(raw).trim())) {
        return MEMBERSHIP_STATUS_LABELS[Number(raw)] ?? String(raw);
    }
    return String(raw);
}

/** Does this status match any of the given names? Case-insensitive. */
export function isStatus(raw, ...names) {
    const s = membershipStatusStr(raw);
    if (!s) return false;
    return names.some(n => s === String(n).toLowerCase().replace(/\s+/g, ''));
}

/**
 * Comparator-safe sort key. Always a string, so `.localeCompare` is always
 * callable — which is the specific thing that broke.
 */
export function membershipStatusSortKey(raw) {
    return membershipStatusStr(raw);
}

/**
 * Is this membership request still waiting for an answer? Approve / Decline
 * show only while it is (owner 2026-09-26: not on Accepted either), and the
 * red Requests count on the sidebar and the Org Admin tab counts exactly
 * these — one rule, so the number always matches the rows with buttons.
 */
export function isOpenMembershipRequest(req) {
    return !isStatus(req?.status, 'accepted', 'approved', 'declined', 'rejected');
}

/** How many of these requests are still waiting for an answer. */
export function countOpenMembershipRequests(requests) {
    return (requests || []).filter(isOpenMembershipRequest).length;
}
